import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Shell from "../components/Shell.jsx";
import { api } from "../lib/api.js";
import { centsToDisplay } from "../lib/money.js";
import { inputCls } from "../components/ui.jsx";

// "Shouldn't Net equal my bank balance?" No — and this page is the answer
// written out. Net is a flow over a year; a balance is a stock. The walk below
// names every item that moves one without moving the other, and the audit
// underneath looks for the one way this app can count revenue twice.

const SEVERITY = {
  high: { ring: "border-red-200 bg-red-50", dot: "bg-red-500", label: "text-red-700" },
  medium: { ring: "border-amber-200 bg-amber-50", dot: "bg-amber-500", label: "text-amber-700" },
  info: { ring: "border-slate-200 bg-slate-50", dot: "bg-slate-400", label: "text-slate-600" },
};

function Money({ cents, className = "" }) {
  const neg = cents < 0;
  return (
    <span className={`tabular-nums ${className}`}>
      {neg ? "−" : ""}
      {centsToDisplay(Math.abs(cents))}
    </span>
  );
}

// Balances are the rows the bridge sums into its bank total, so a duplicate
// here overstates the bank and understates the unexplained line. Until BL-9 a
// balance key could only be written, so a stale label was permanent. Merge is
// the important one: it folds two names for one account into the name the
// operator chose, carrying the account id across so later imports update that
// row instead of re-creating the other.
function ManageRow({ b, others, onAction, busy }) {
  const [mode, setMode] = useState(null);
  const [name, setName] = useState(b.account);

  if (mode === "rename") {
    return (
      <div className="flex items-center gap-2 border-t border-slate-100 py-2">
        <input
          className={inputCls}
          value={name}
          onChange={(e) => setName(e.target.value)}
          aria-label={`New name for ${b.account}`}
        />
        <button type="button" disabled={busy} className="text-xs font-medium text-navy-700"
          onClick={() => onAction({ action: "rename", from: b.account, to: name }).then(() => setMode(null))}>
          Save
        </button>
        <button type="button" className="text-xs text-slate-400" onClick={() => { setName(b.account); setMode(null); }}>
          Cancel
        </button>
      </div>
    );
  }

  if (mode === "merge") {
    return (
      <div className="border-t border-slate-100 py-2">
        <p className="text-[11px] text-slate-500">Fold “{b.account}” into which account? It keeps that name.</p>
        <div className="mt-1 flex flex-wrap gap-1.5">
          {others.map((o) => (
            <button key={o.account} type="button" disabled={busy}
              className="rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-700"
              onClick={() => onAction({ action: "merge", from: b.account, into: o.account })}>
              {o.account}
            </button>
          ))}
          <button type="button" className="px-2 py-1 text-xs text-slate-400" onClick={() => setMode(null)}>Cancel</button>
        </div>
      </div>
    );
  }

  // The label is never truncated here: telling two near-identical names apart
  // ("VIA Member Shares" vs "VIA Checking — Savings") is the whole task on this
  // screen, and at phone width an ellipsis hides exactly the deciding part.
  return (
    <div className="border-t border-slate-100 py-2">
      <div className="flex items-baseline justify-between gap-2">
        <p className="break-words text-xs font-medium text-slate-700">{b.account}</p>
        <span className="shrink-0 text-xs text-slate-600"><Money cents={b.amount_cents} /></span>
      </div>
      <p className="mt-0.5 text-[11px] text-slate-400">
        as of {b.as_of} · {b.acctid ? `linked to account ${b.acctid}` : "not linked to an account yet"}
      </p>
      <div className="mt-1 flex items-center gap-3">
        <button type="button" className="text-[11px] text-navy-700" onClick={() => setMode("rename")}>Rename</button>
        {others.length > 0 && (
          <button type="button" className="text-[11px] text-navy-700" onClick={() => setMode("merge")}>Merge</button>
        )}
        <button type="button" disabled={busy} className="text-[11px] text-red-600"
          onClick={() => { if (confirm(`Remove the stored balance for “${b.account}”? The next import can re-create it.`)) onAction({ action: "delete", account: b.account }); }}>
          Remove
        </button>
      </div>
    </div>
  );
}

function BalanceHeader({ balances, primary, onChanged }) {
  const [managing, setManaging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const act = async (body) => {
    setBusy(true);
    setErr(null);
    try {
      await api("/api/money/balances", { method: "POST", body });
      await onChanged();
      setManaging(true);
    } catch (e) {
      setErr(e.message || "That did not work");
    } finally {
      setBusy(false);
    }
  };

  if (!balances?.length) {
    return (
      <section className="rounded-2xl bg-white p-4 shadow-sm">
        <p className="text-sm text-slate-600">
          No statement has been imported yet, so there is no bank-verified balance to reconcile against.
        </p>
        <Link to="/money?tab=import" className="mt-2 inline-block text-sm font-medium text-navy-700">
          Import a statement →
        </Link>
      </section>
    );
  }

  const total = balances.reduce((s, b) => s + b.amount_cents, 0);
  // Two rows holding the same figure are usually one account under two names.
  // Usually, not always: say so, and let the operator decide.
  const sameAmount = balances.filter((b) => balances.some((o) => o !== b && o.amount_cents === b.amount_cents));

  return (
    <section className="rounded-2xl bg-white p-4 shadow-sm">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Bank balance, as imported</p>
      <p className="text-3xl font-semibold text-navy-700"><Money cents={total} /></p>

      {!managing && (
        <div className="mt-3 space-y-1 border-t border-slate-100 pt-2">
          {balances.map((b) => (
            <div key={b.account} className="flex items-baseline justify-between gap-2 text-xs">
              <span className="truncate text-slate-500">{b.account} · as of {b.as_of}</span>
              <span className="shrink-0 text-slate-600"><Money cents={b.amount_cents} /></span>
            </div>
          ))}
        </div>
      )}

      {managing && (
        <div className="mt-3">
          {balances.map((b) => (
            <ManageRow key={b.account} b={b} busy={busy}
              others={balances.filter((o) => o.account !== b.account)} onAction={act} />
          ))}
        </div>
      )}

      {err && <p className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-[11px] text-red-700">{err}</p>}

      {sameAmount.length > 1 && (
        <p className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-[11px] leading-snug text-amber-800">
          {sameAmount.length} balances hold the same figure. If they are one real account under two
          names, merge them — the bank total above is counting that money twice. Two accounts can
          genuinely hold the same amount, so this is a prompt, not a verdict.
        </p>
      )}

      <div className="mt-2 flex items-baseline justify-between">
        <p className="text-[11px] text-slate-400">
          Straight from your last statement import{primary ? `, ${primary.as_of}` : ""}. Anything booked
          since then is not deducted from it yet.
        </p>
        <button type="button" className="shrink-0 pl-3 text-[11px] font-medium text-navy-700"
          onClick={() => setManaging((v) => !v)}>
          {managing ? "Done" : "Manage"}
        </button>
      </div>

      {managing && (
        <p className="mt-2 rounded-lg bg-slate-50 px-3 py-2 text-[11px] leading-snug text-slate-500">
          <strong>Import first, then merge.</strong> A merge carries the bank's own account number onto
          the row you keep, and only an imported statement supplies that number. Merge before the
          import and the next import will add the duplicate straight back.
        </p>
      )}
    </section>
  );
}

function Bridge({ data }) {
  const { steps, operating_cash_cents, bank_total_cents, opening_or_unexplained_cents, year } = data;
  return (
    <section className="rounded-2xl bg-white p-4 shadow-sm">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Net income → cash</p>
      <div className="mt-3 space-y-2">
        {steps.map((s) => (
          <div key={s.key}>
            <div className="flex items-baseline justify-between gap-3">
              <span className={`text-sm ${s.effect === "start" ? "font-medium text-slate-800" : "text-slate-600"}`}>
                {s.label}
                {s.count ? <span className="text-slate-400"> ({s.count})</span> : null}
              </span>
              <span className={`text-sm ${s.effect === "start" ? "font-semibold text-slate-900" : "text-slate-700"}`}>
                <Money cents={s.cents} />
              </span>
            </div>
            <p className="mt-0.5 text-[11px] leading-snug text-slate-400">{s.note}</p>
          </div>
        ))}
      </div>

      <div className="mt-3 flex items-baseline justify-between gap-3 border-t border-slate-200 pt-2">
        <span className="text-sm font-medium text-slate-800">Cash {year} should have produced</span>
        <span className="text-sm font-semibold text-slate-900">
          <Money cents={operating_cash_cents} />
        </span>
      </div>

      {bank_total_cents !== null && (
        <>
          <div className="mt-1 flex items-baseline justify-between gap-3">
            <span className="text-sm text-slate-600">Actually in the bank</span>
            <span className="text-sm text-slate-700">
              <Money cents={bank_total_cents} />
            </span>
          </div>
          <div className="mt-3 rounded-xl bg-slate-50 px-3 py-2">
            <div className="flex items-baseline justify-between">
              <span className="text-sm font-medium text-slate-700">Opening balance, or unexplained</span>
              <span className="text-sm font-semibold text-slate-900">
                <Money cents={opening_or_unexplained_cents} />
              </span>
            </div>
            <p className="mt-1 text-[11px] leading-snug text-slate-500">
              What the bank holds that {year} did not produce. Only the latest statement snapshot is stored, so the
              opening balance cannot be computed — it lands here. If this is roughly what you started {year} with, the
              books reconcile. If it is not, the difference is the size of the problem.
            </p>
          </div>
        </>
      )}
    </section>
  );
}

// One flagged row, and the evidence behind it. The operator would not click an
// irreversible "Not income" until he could see the revenue was captured
// somewhere else — correctly, so the answer now lives on the row he is acting
// on rather than on another screen he has to hold in his head.
const CONFIDENCE = {
  exact: { tone: "text-emerald-700", lead: "Already in your books as this Stripe charge" },
  batch: { tone: "text-emerald-700", lead: "Already in your books as these Stripe charges, which total this payout exactly" },
  near: { tone: "text-amber-700", lead: "Almost certainly this Stripe charge, but the amounts differ" },
  unmatched: { tone: "text-amber-700", lead: "No Stripe charge matches this amount. These are the charges near that date — check before removing" },
};

function BookedFrom({ b }) {
  if (!b) return null;
  const c = CONFIDENCE[b.confidence] || CONFIDENCE.unmatched;
  return (
    <div className="rounded-xl bg-white/70 px-3 py-2">
      <p className={`text-[11px] font-semibold leading-snug ${c.tone}`}>{c.lead}</p>
      {b.charges.length === 0 ? (
        <p className="mt-1 text-[11px] text-slate-500">Nothing synced from Stripe in the fortnight before this payout.</p>
      ) : (
        <>
          <ul className="mt-1 space-y-1">
            {b.charges.map((ch) => (
              <li key={ch.id} className="text-[11px] leading-snug text-slate-600">
                <span className="tabular-nums font-medium text-slate-700">{centsToDisplay(ch.gross_cents)}</span>
                {" · "}
                {ch.date}
                {ch.payer ? ` · ${ch.payer}` : ""}
                {ch.description ? ` · ${ch.description}` : ""}
                <span className="block text-[10px] text-slate-400">
                  less {centsToDisplay(ch.fee_cents)} fee = {centsToDisplay(ch.net_cents)} paid out
                  {ch.booked ? " · booked as income" : " · NOT yet booked as income"}
                </span>
              </li>
            ))}
          </ul>
          {b.confidence === "near" && (
            <p className="mt-1.5 text-[11px] leading-snug text-amber-800">
              That charge should have paid out {centsToDisplay(b.charges[0].net_cents)}, but{" "}
              {centsToDisplay(Math.abs(b.difference_cents))} {b.difference_cents > 0 ? "less" : "more"} arrived. Stripe keeps a
              separate service fee on some payments — that gap is usually it, and it belongs in your expenses.
            </p>
          )}
          {b.confidence !== "unmatched" && (
            <p className="mt-1.5 border-t border-slate-200 pt-1.5 text-[11px] leading-snug text-slate-600">
              Retiring the deposit keeps <span className="font-semibold">{centsToDisplay(b.gross_cents)}</span> of income and
              the <span className="font-semibold">{centsToDisplay(b.fee_cents)}</span> fee deduction. Gross income plus the fee
              as an expense is the better record — the net deposit alone would lose you that deduction.
            </p>
          )}
        </>
      )}
      <p className="mt-1.5 text-[10px] leading-snug text-slate-400">
        Stripe does not record which charges a payout carried, so this pairing is worked out from the amount and the date.
      </p>
    </div>
  );
}

// What to call a row that has no vendor, memo or description — an owner
// contribution typed by hand, typically. Its type is the honest label; a bare
// row id is not something the operator can act on.
const TYPE_LABEL = { income: "Income", expense: "Expense", owner_draw: "Owner draw", owner_contribution: "Owner contribution" };
const rowLabel = (r) => r.vendor || r.memo || r.description || r.sources || TYPE_LABEL[r.type] || r.id;

// One action set per completeness finding (BL-29). Each button is the write
// the finding's own text asks for; the server re-derives eligibility and
// returns the recomputed screen. Destructive ones confirm and say what is
// reversible: Ignore has Review again as its way back, Remove does not.
function RowActions({ r, finding, busy, onAct, methods }) {
  const btn = "font-medium disabled:opacity-40 whitespace-nowrap";
  if (finding === "bank_lines_without_entry") {
    return (
      <span className="inline-flex flex-wrap items-center justify-end gap-x-3 gap-y-1">
        <button
          type="button"
          disabled={busy}
          className={`${btn} text-red-700`}
          onClick={() =>
            confirm(
              `Ignore the ${centsToDisplay(Math.abs(r.amount_cents))} line dated ${r.posted_date}?\n\n` +
                `For a transfer, personal money, or account setup. The line stays on record as ignored and no entry is created. ` +
                `If you change your mind, Review again puts it back in the Bank queue.`,
            ) && onAct({ action: "ignore_line", bank_txn_id: r.id }, "Could not ignore that line")
          }
        >
          Ignore
        </button>
        <button type="button" disabled={busy} className={`${btn} text-navy-700`} onClick={() => onAct({ action: "reopen_line", bank_txn_id: r.id }, "Could not reopen that line")}>
          Review again
        </button>
      </span>
    );
  }
  if (finding === "entries_without_bank_line") {
    return (
      <span className="inline-flex flex-wrap items-center justify-end gap-x-3 gap-y-1">
        <select
          disabled={busy}
          value=""
          aria-label="Paid by"
          className="max-w-[9rem] rounded-md border border-slate-300 bg-white px-1 py-0.5 text-[11px] text-slate-700"
          onChange={(e) => e.target.value && onAct({ action: "set_payment_method", transaction_id: r.id, payment_method: e.target.value }, "Could not set the payment method")}
        >
          <option value="">Paid by…</option>
          {methods.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
        <Link to={`/edit/${r.id}`} className={`${btn} text-navy-700`}>
          Open in Ledger
        </Link>
          <button
            type="button"
            disabled={busy}
            className={`${btn} text-red-700`}
            onClick={() =>
              confirm(
                `Remove the ${centsToDisplay(r.amount_cents)} ${TYPE_LABEL[r.type]?.toLowerCase() || "entry"} dated ${r.date}?\n\n` +
                  `For an entry nothing paid \u2014 a duplicate or an error. This cannot be undone from the app.`,
              ) && onAct({ action: "remove_entry", transaction_id: r.id }, "Could not remove that entry")
            }
          >
            Remove
          </button>
      </span>
    );
  }
  if (finding === "bank_paid_but_marked_personal") {
    return (
      <span className="inline-flex flex-wrap items-center justify-end gap-x-3 gap-y-1">
        <button
          type="button"
          disabled={busy}
          className={`${btn} text-navy-700`}
          onClick={() => onAct({ action: "set_payment_method", transaction_id: r.id, payment_method: r.source_label }, "Could not set the payment method")}
        >
          Bank paid it
        </button>
        <button
          type="button"
          disabled={busy}
          className={`${btn} text-red-700`}
          onClick={() =>
            confirm(
              `Unmatch the ${r.source_label} line dated ${r.posted_date} from this entry?\n\n` +
                `The line goes back to the Bank queue and the entry stays, unreconciled. Nothing is deleted.`,
            ) && onAct({ action: "unmatch_line", bank_txn_id: r.bank_txn_id }, "Could not unmatch that line")
          }
        >
          Wrong match
        </button>
      </span>
    );
  }
  return null;
}

const ACTIONABLE = new Set(["bank_lines_without_entry", "entries_without_bank_line", "bank_paid_but_marked_personal"]);

function FindingRow({ r, finding, resolvable, busy, onResolve, onAct, methods }) {
  const [open, setOpen] = useState(false);
  const expandable = !!r.booked_from;
  const actionable = ACTIONABLE.has(finding);
  const cols = 3 + (r.n ? 1 : 0) + (resolvable ? 1 : 0);
  return (
    <>
      <tr className="border-t border-white/60">
        <td className="py-1 pr-2 whitespace-nowrap align-top">{r.date || r.posted_date || r.payout_date}</td>
        <td className="py-1 pr-2 tabular-nums whitespace-nowrap align-top">
          <Money cents={Math.abs(r.amount_cents ?? r.gross_cents ?? 0)} />
        </td>
        <td className="py-1 pr-2 align-top">
          {expandable ? (
            <button type="button" onClick={() => setOpen((v) => !v)} className="text-left underline decoration-dotted underline-offset-2">
              {rowLabel(r)}
              <span className="ml-1 text-slate-400">{open ? "▾" : "▸"}</span>
            </button>
          ) : (
            rowLabel(r)
          )}
          {r.note ? <span className="block text-[10px] leading-tight text-slate-500">{r.note}</span> : null}
          {r.payout_matches > 1 ? (
            <span className="block text-[10px] leading-tight text-amber-700">
              {r.payout_matches} payouts share this amount — check which one before removing
            </span>
          ) : null}
        </td>
        {r.n ? <td className="py-1 text-right whitespace-nowrap align-top">×{r.n}</td> : null}
        {resolvable && (
          <td className="py-1 text-right whitespace-nowrap align-top">
            <button
              type="button"
              disabled={busy}
              className="font-medium text-red-700 disabled:opacity-40"
              onClick={() =>
                confirm(
                  `Remove the ${centsToDisplay(r.amount_cents)} income entry dated ${r.date}?\n\n` +
                    `It is the payout of money already counted when the client paid. The bank line stays, as a transfer. This cannot be undone from the app.` +
                    (r.payout_matches > 1
                      ? `\n\n${r.payout_matches} payouts share this amount and date range. Removing this entry settles the deposit once — it does not delete the other payouts.`
                      : ""),
                ) && onResolve(r)
              }
            >
              Not income
            </button>
          </td>
        )}
      </tr>
      {actionable && (
        <tr>
          <td colSpan={cols} className="pb-2 text-right">
            <RowActions r={r} finding={finding} busy={busy} onAct={onAct} methods={methods} />
          </td>
        </tr>
      )}
      {open && (
        <tr>
          <td colSpan={cols} className="pb-2">
            <BookedFrom b={r.booked_from} />
          </td>
        </tr>
      )}
    </>
  );
}

// What the completeness check could see. A clean result is a claim about these
// dates and no others — an entry from a month with no statement is unverified,
// not verified (Learning #19: say how much is behind a list, and here, how
// much is behind a "nothing found").
function Coverage({ c }) {
  if (!c?.coverage?.length) {
    return <p className="mt-1 text-[11px] text-slate-400">No statement imported yet, so entries cannot be checked against the bank.</p>;
  }
  return (
    <p className="mt-1 text-[11px] text-slate-400">
      Statements cover{" "}
      {c.coverage.map((s, i) => (
        <span key={s.source_label}>
          {i > 0 ? "; " : ""}
          <span className="text-slate-500">{s.source_label}</span> {s.first_date} to {s.last_date} ({s.lines} line{s.lines === 1 ? "" : "s"})
        </span>
      ))}
      . Entries outside those dates are not checked.
    </p>
  );
}

function Finding({ f, onResolve, onAct, methods, busy }) {
  const s = SEVERITY[f.severity] || SEVERITY.info;
  const [open, setOpen] = useState(f.severity === "high");
  // Only this finding has a single unambiguous repair: the deposit is the payout
  // of money already counted, so the row comes out and the bank line goes back to
  // being a transfer. The others need a human to decide which copy is real.
  const resolvable = f.key === "payout_booked_as_income";
  return (
    <div className={`rounded-xl border p-3 ${s.ring}`}>
      <button type="button" onClick={() => setOpen((v) => !v)} className="flex w-full items-start gap-2 text-left">
        <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${s.dot}`} />
        <span className="flex-1">
          <span className={`block text-sm font-medium ${s.label}`}>{f.title}</span>
          {f.total_cents ? (
            <span className="block text-xs text-slate-500">
              <Money cents={f.total_cents} /> in question
            </span>
          ) : null}
        </span>
        <span className="text-slate-400">{open ? "−" : "+"}</span>
      </button>
      {open && (
        <div className="mt-2 space-y-2 border-t border-white/60 pt-2">
          <p className="text-xs leading-snug text-slate-600">{f.detail}</p>
          <p className="text-xs font-medium leading-snug text-slate-700">{f.action}</p>
          {f.link && (
            <Link
              to={f.link.href}
              className="inline-block rounded-lg bg-navy-700 px-3 py-1.5 text-[11px] font-semibold text-white"
            >
              {f.link.label} →
            </Link>
          )}
          {f.rows?.length ? (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-[11px] text-slate-600">
                <tbody>
                  {f.rows.slice(0, 25).map((r, i) => (
                    <FindingRow key={r.id || i} r={r} finding={f.key} resolvable={resolvable} busy={busy} onResolve={onResolve} onAct={onAct} methods={methods} />
                  ))}
                </tbody>
              </table>
              {f.rows.length > 25 && <p className="pt-1 text-[11px] text-slate-400">…and {f.rows.length - 25} more.</p>}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

export default function Reconcile() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());

  // Managing a balance changes the bank total, so the whole bridge is refetched
  // rather than patched — a half-updated reconciliation is worse than a pause.
  const load = useCallback(
    async (clear = true) => {
      if (clear) setData(null);
      setErr(null);
      try {
        setData(await api(`/api/reports/reconcile?year=${year}`));
      } catch (e) {
        setErr(e.message || "Could not load the reconciliation");
      }
    },
    [year],
  );

  useEffect(() => {
    load();
  }, [load]);

  const [resolving, setResolving] = useState(false);
  // The response carries the recomputed bridge and audit, so the screen settles
  // on server truth rather than on a local guess about what the write did.
  const act = async (body, failed = "Could not resolve that entry") => {
    setResolving(true);
    setErr(null);
    try {
      setData(await api("/api/reports/reconcile", { method: "POST", body: { ...body, year } }));
    } catch (e) {
      setErr(e.message || failed);
      await load(false);
    } finally {
      setResolving(false);
    }
  };
  const resolve = (row) => act({ action: "resolve_payout_as_income", transaction_id: row.id, payout_id: row.payout_id });

  // Years the ledger actually has, from the server. Falls back to the viewed
  // year alone rather than inventing empty ones.
  const years = data?.years?.length ? data.years : [year];

  return (
    <Shell title="Reconcile">
      <div className="space-y-4 p-4">
        <div className="flex items-center gap-2">
          {years.map((y) => (
            <button
              key={y}
              type="button"
              onClick={() => setYear(y)}
              className={`rounded-full px-3 py-1 text-xs font-medium ${
                y === year ? "bg-navy-700 text-white" : "bg-white text-slate-600 shadow-sm"
              }`}
            >
              {y}
            </button>
          ))}
        </div>

        {err && <p className="rounded-xl bg-red-50 p-3 text-sm text-red-700">{err}</p>}
        {!data && !err && <p className="animate-pulse text-sm text-slate-400">Reconciling…</p>}

        {data && (
          <>
            <BalanceHeader balances={data.balances} primary={data.primary} onChanged={() => load(false)} />
            <Bridge data={data} />

            <section className="rounded-2xl bg-white p-4 shadow-sm">
              <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Books audit</p>
              <Coverage c={data.audit.completeness} />
              {data.audit.clean ? (
                <p className="mt-2 text-sm text-slate-600">
                  Nothing found. No bank deposit booked as income matches a Stripe payout, no two entries share a date,
                  amount and type
                  {data.audit.completeness?.coverage?.length
                    ? ", and every entry booked as bank money inside the dates your statements cover has a statement line behind it."
                    : ". Whether every entry has a statement line behind it cannot be checked until a statement is imported."}
                </p>
              ) : (
                <div className="mt-3 space-y-2">
                  {data.audit.findings.map((f) => (
                    <Finding key={f.key} f={f} busy={resolving} onResolve={resolve} onAct={act} methods={data.audit.completeness?.payment_methods || []} />
                  ))}
                </div>
              )}
              {!data.audit.clean && data.audit.findings.length > 0 && (
                <p className="mt-3 text-[11px] leading-snug text-slate-400">
                  Every item is a question, not a verdict. Two identical charges on one day can both be real.
                </p>
              )}
            </section>

            <p className="text-center text-[11px] text-slate-400">{data.disclaimer}.</p>
          </>
        )}
      </div>
    </Shell>
  );
}
